// Config
import endpoints from '@/config/endpoints'

// Base class
import BaseAPIService from './BaseAPIService'

// Types
import { Document, DocumentsResponse } from './DocumentsService.interface'

class DocumentsService extends BaseAPIService {
  constructor() {
    super(endpoints.docs)
  }

  /**
   * Generate mock documents for testing all document types in development mode.
   * 
   * This method returns a comprehensive set of mock documents including:
   * - Standard documents: booking-confirmation, vouchers, invoice, other
   * - New document types: flight_tickets, flight_tickets_aller, flight_tickets_retour, flight_tickets_change, skier_insurance_certificate
   * - Altibus tickets: altibus_ticket_aller, altibus_ticket_retour
   * - TNE e-tickets: tne_eticket_aller, tne_eticket_retour
   * - Insurance: cancellation_insurance_certificate
   * 
   * @param bookingReference - The booking reference to associate with mock documents
   * @returns Array of mock Document objects for testing
   * 
   * @example
   * // Enable mock mode in browser console (development only):
   * window.MOCK_DOCUMENTS = true
   * // Then refresh or reopen the booking panel
   */
  private getMockDocuments(bookingReference: string): Document[] {
    return [
      {
        documentType: 'booking-confirmation' as any,
        label: 'Confirmation de réservation',
        fileLink: 'https://example.com/booking-confirmation.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'vouchers' as any,
        label: 'Documents de voyage',
        fileLink: 'https://example.com/vouchers.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'invoice' as any,
        label: 'Facture acquittée',
        fileLink: 'https://example.com/invoice.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'flight_tickets' as any,
        label: 'Billets d\'avion',
        fileLink: 'https://example.com/flight-tickets.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'flight_tickets_aller' as any,
        label: 'Billets d\'avion (aller)',
        fileLink: 'https://example.com/flight-tickets-aller.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'flight_tickets_retour' as any,
        label: 'Billets d\'avion (retour)',
        fileLink: 'https://example.com/flight-tickets-retour.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'flight_tickets_change' as any,
        label: 'Changement d\'horaire vol',
        fileLink: 'https://example.com/flight-tickets-change.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'skier_insurance_certificate' as any,
        label: 'Certificat d\'assurance skieur',
        fileLink: 'https://example.com/skier-insurance.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'altibus_ticket_aller' as any,
        label: 'Billet Altibus aller',
        fileLink: 'https://example.com/altibus-aller.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'altibus_ticket_retour' as any,
        label: 'Billet Altibus retour',
        fileLink: 'https://example.com/altibus-retour.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'tne_eticket_aller' as any,
        label: 'E-billet Train aller',
        fileLink: 'https://example.com/tne-aller.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'tne_eticket_retour' as any,
        label: 'E-billet Train retour',
        fileLink: 'https://example.com/tne-retour.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'cancellation_insurance_certificate' as any,
        label: 'Document d\'assurance',
        fileLink: 'https://example.com/cancellation-insurance.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
      {
        documentType: 'other' as any,
        label: 'Autre document',
        fileLink: 'https://example.com/other.pdf',
        bookingReference,
        isActive: true,
        includeInVoucher: false,
      },
    ]
  }

  /**
   * Fetch documents for a single booking reference (internal helper method).
   * 
   * @param bookingReference - The booking reference to fetch documents for
   * @returns Promise resolving to an array of Document objects (may be empty)
   * @private
   */
  private async fetchDocumentsForReference(bookingReference: string): Promise<Document[]> {
    if (!bookingReference) {
      return []
    }

    try {
      const { data } = await this.apiClient.get<DocumentsResponse | { message: string }>('', {
        params: {
          bookingReference,
        },
        headers: this.getDefaultHeaders(),
      })

      // Handle case where API returns a message object instead of an array
      if (!Array.isArray(data)) {
        if (data && typeof data === 'object' && 'message' in data) {
          console.log(`No documents found for booking ${bookingReference}: ${(data as { message: string }).message}`)
          return []
        }
        throw new Error(`Unexpected response format: expected array or message object, got ${typeof data}`)
      }

      // Filter out inactive documents (v3 API feature)
      return data.filter(doc => doc.isActive !== false)
    } catch (err: any) {
      // Handle 404 errors with message in response body
      if (err?.response?.status === 404) {
        const responseData = err?.response?.data
        if (responseData && typeof responseData === 'object' && 'message' in responseData) {
          const message = (responseData as { message: string }).message
          console.log(`No documents found for booking ${bookingReference}: ${message}`)
          return []
        }
        // 404 without message - still treat as no documents
        console.log(`No documents found for booking ${bookingReference} (404)`)
        return []
      }
      
      // For all other errors, log and rethrow
      console.error(`Error fetching documents for ${bookingReference}:`, err)
      throw err
    }
  }

  /**
   * Fetch active documents for a booking reference, optionally including BO reference.
   * 
   * Supports dual reference fetching: fetches from both primary reference and BO reference,
   * then combines and deduplicates by document identity (id / fileLink).
   * 
   * Supports mock mode in development for testing all document types.
   * Mock mode is automatically disabled in production builds.
   * 
   * @param bookingReference - The primary booking reference to fetch documents for
   * @param boReference - Optional BO (Back Office) reference to also fetch documents from
   * @returns Promise resolving to an array of active Document objects
   * 
   * @remarks
   * Dual Reference Fetching:
   * - If boReference is provided and different from bookingReference, fetches from both
   * - Uses Promise.allSettled to handle failures gracefully
   * - Deduplicates by id/fileLink (NOT documentType) so the same type can appear twice
   *   under DX + BO refs (e.g. two flight_tickets PDFs)
   * 
   * Mock Mode (Development Only):
   * - Set `window.MOCK_DOCUMENTS = true` in browser console
   * - Or set `VUE_APP_MOCK_DOCUMENTS=true` in .env.development
   * - Returns mock documents instead of making API call
   * - Automatically disabled in production (NODE_ENV === 'production')
   * 
   * Error Handling:
   * - 404 responses with "No documents" message are treated as empty array (not an error)
   * - Other errors are logged and rethrown
   */
  public async getActiveDocuments(bookingReference: string, boReference?: string): Promise<Document[]> {
    // Mock mode: Only available in development mode for testing
    // Set window.MOCK_DOCUMENTS = true in browser console to test all document types
    // This will NOT work in production (NODE_ENV === 'production')
    const isDevelopment = process.env.NODE_ENV !== 'production'
    const mockFlag = isDevelopment && ((window as any).MOCK_DOCUMENTS === true || process.env.VUE_APP_MOCK_DOCUMENTS === 'true')
    
    if (mockFlag) {
      console.log('🔧🔧🔧 MOCK MODE ENABLED (DEV ONLY): Returning mock documents for testing')
      console.log('🔧 Mock documents will include all new document types')
      // Simulate async delay
      await new Promise(resolve => setTimeout(resolve, 100))
      const mockDocs = this.getMockDocuments(bookingReference)
      console.log('🔧 Returning', mockDocs.length, 'mock documents:', mockDocs.map(d => d.documentType))
      return mockDocs
    }

    try {
      if (!bookingReference) {
        throw new Error('bookingReference cannot be undefined in getActiveDocuments')
      }

      // Determine if we should make a second call for BO reference
      const shouldMakeSecondCall = boReference && 
        boReference.trim() !== '' &&
        boReference !== bookingReference

      // Primary call (always made)
      const primaryCall = this.fetchDocumentsForReference(bookingReference)

      // Secondary call (only if conditions are met)
      const secondaryCall = shouldMakeSecondCall
        ? this.fetchDocumentsForReference(boReference)
        : Promise.resolve([])

      // Execute both calls in parallel using Promise.allSettled
      // This ensures one failed call doesn't prevent the other from completing
      const [primaryResponse, secondaryResponse] = await Promise.allSettled([
        primaryCall,
        secondaryCall
      ])

      // Extract data from responses (handle failures gracefully)
      const primaryDocuments = primaryResponse.status === 'fulfilled' 
        ? primaryResponse.value 
        : []
      
      const secondaryDocuments = secondaryResponse.status === 'fulfilled' 
        ? secondaryResponse.value 
        : []

      // If secondary call failed, log it but don't throw (we still have primary results)
      if (secondaryResponse.status === 'rejected' && shouldMakeSecondCall) {
        console.warn(`Failed to fetch documents for BO reference ${boReference}, using primary reference only:`, secondaryResponse.reason)
      }

      // Merge DX + BO results; dedupe by document identity (id/fileLink), NOT documentType —
      // same type can exist under both refs (e.g. two flight_tickets PDFs).
      const documentIdentity = (doc: Document): string => {
        if (doc?.id != null) return `id:${doc.id}`
        if (doc?.fileLink) return `link:${doc.fileLink}`
        return `type:${doc?.documentType}:${doc?.bookingReference || ''}:${doc?.label || ''}`
      }

      const combinedDocuments: Document[] = []
      const seen = new Set<string>()
      for (const doc of [...primaryDocuments, ...secondaryDocuments]) {
        const key = documentIdentity(doc)
        if (seen.has(key)) continue
        seen.add(key)
        combinedDocuments.push(doc)
      }

      if (shouldMakeSecondCall) {
        console.log(`📄 Documents fetched: ${primaryDocuments.length} from primary (${bookingReference}), ${secondaryDocuments.length} from BO (${boReference}), ${combinedDocuments.length} total after deduplication`)
      } else {
        console.log(`📄 Documents fetched: ${combinedDocuments.length} from primary reference (${bookingReference})`)
      }

      return combinedDocuments
    } catch (err: any) {
      // For all other errors, log and rethrow
      console.error('Error fetching documents:', err)
      throw err
    }
  }

  protected override getDefaultHeaders() {
    // Call the parent method to get the default headers
    const defaultHeaders = super.getDefaultHeaders()

    // Remove the 'locale' property
    delete defaultHeaders.locale
    delete defaultHeaders.Accept

    // Add or overwrite other headers
    return {
      ...defaultHeaders,
    }
  }
}

export default DocumentsService
